import React, { useRef, useEffect, useState } from 'react';
import type { User, Message } from '../types';
import SendIcon from './icons/SendIcon';
import { useAuth } from '../contexts/AuthProvider';
import { formatMessageTimestamp } from '../utils/formatTimestamp';
import CloudArrowDownIcon from './icons/CloudArrowDownIcon';


interface ChatWindowProps {
  partner: User;
  messages: Message[];
  onSendMessage: (text: string) => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ partner, messages, onSendMessage }) => {
  const { currentUser } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScroll = useRef(true);
  const [showNewMessagesAlert, setShowNewMessagesAlert] = useState(false);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container) {
      // Check if user is scrolled near the bottom. A small buffer helps.
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50;
      shouldAutoScroll.current = isAtBottom;
      if (isAtBottom) {
        setShowNewMessagesAlert(false); // Hide alert when user scrolls to the bottom manually
      }
    }
  };

  // On initial load or when switching conversations, scroll to bottom and enable auto-scroll.
  useEffect(() => {
    if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        shouldAutoScroll.current = true;
        setShowNewMessagesAlert(false);
    }
  }, [partner]);

  // When new messages arrive, scroll to bottom only if user hasn't scrolled up.
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const isNewMessageFromPartner = lastMessage && lastMessage.senderHandle === partner.handle;

    if (shouldAutoScroll.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (isNewMessageFromPartner) {
      setShowNewMessagesAlert(true);
    }
  }, [messages, partner.handle]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim()) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
      setShowNewMessagesAlert(false);
      shouldAutoScroll.current = true;
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowNewMessagesAlert(false);
    shouldAutoScroll.current = true;
  };

  return (
    <div className="flex flex-col flex-grow min-h-0 bg-brand-surface rounded-lg relative">
      <div className="flex-shrink-0 flex items-center p-4 border-b border-gray-700/50">
        <img src={partner.avatarUrl} alt={partner.name} className="h-10 w-10 rounded-full" />
        <div className="ml-3">
          <h2 className="font-bold text-lg text-white">{partner.name}</h2>
          <p className="text-sm text-brand-muted">@{partner.handle}</p>
        </div>
      </div>
      
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-grow overflow-y-auto p-4 space-y-2"
      >
        {messages.map((msg, index) => {
            const isMe = msg.senderHandle === currentUser?.handle;
            return (
                <div key={msg.id || index} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                   <div className={`flex items-end gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                       {!isMe && <img src={partner.avatarUrl} alt={partner.name} className="h-6 w-6 rounded-full"/>}
                       <div className={`max-w-xs md:max-w-md p-3 rounded-xl ${isMe ? 'bg-brand-orange text-white rounded-br-none' : 'bg-gray-700 text-white rounded-bl-none'}`}>
                            <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                       </div>
                   </div>
                   <span className="text-xs text-brand-muted px-2 pt-1">
                       {formatMessageTimestamp(msg.timestamp)}
                   </span>
                </div>
            )
        })}
        <div ref={messagesEndRef} />
      </div>

      {showNewMessagesAlert && (
        <button
            onClick={scrollToBottom}
            className="absolute bottom-24 md:bottom-20 left-1/2 -translate-x-1/2 z-10 bg-blue-500 text-white font-semibold px-4 py-2 rounded-full shadow-lg flex items-center space-x-2 animate-bounce"
        >
            <CloudArrowDownIcon className="h-5 w-5" />
            <span>New Messages</span>
        </button>
      )}


      <div className="flex-shrink-0 p-4 border-t border-gray-700/50">
        <form onSubmit={handleSend} className="flex items-center space-x-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="w-full bg-gray-700 text-white rounded-full py-3 px-5 focus:outline-none focus:ring-2 focus:ring-brand-orange"
            autoComplete="off"
          />
          <button type="submit" disabled={!newMessage.trim()} className="bg-brand-orange p-3 rounded-full text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
            <SendIcon className="h-6 w-6" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatWindow;